// CL8 (docs/build-briefs/CL8-card-reader-health.md): the words a card reader's health is shown in. Pure and DOM-free, so each
// word is proven by a unit test. The server decides the state (src/domain/readerHealth.ts); nothing here infers one.

export type ReaderHealthState = "online" | "offline" | "not_seen" | "unpaired";

/** What GET /tenants/:tenantId/terminals/:terminalId/health and the closeout's reader entries report. */
export interface ReaderHealthDto {
  terminalId: string;
  state: ReaderHealthState;
  lastSeenAt: string | null;
  lastSuccessfulPaymentAt: string | null;
  lastFailedPaymentAt: string | null;
  checkedAt: string;
}

/** How long a floor reading is reused before the next screen change asks again. */
export const READER_HEALTH_REFRESH_MS = 60_000;

/** The floor pill's word for an unhealthy reader, or null when it is healthy or the state is not known. An unpaired reader
 * reads as Not seen on the floor (the closeout says Unpaired). */
export function floorReaderWord(
  state: ReaderHealthState | undefined,
): "Offline" | "Not seen" | null {
  switch (state) {
    case "offline":
      return "Offline";
    case "not_seen":
    case "unpaired":
      return "Not seen";
    default:
      return null;
  }
}

/** The pill's full text: the existing label, then the word after a middle dot when the reader is unhealthy. */
export function readerPillText(label: string, state: ReaderHealthState | undefined): string {
  const word = floorReaderWord(state);
  return word === null ? label : `${label} · ${word}`;
}

/** The short chip a tight header shows: the word when unhealthy, otherwise the usual short label. */
export function readerPillShortText(
  state: ReaderHealthState | undefined,
  healthyShort: string,
): string {
  return floorReaderWord(state) ?? healthyShort;
}

const CLOSEOUT_STATE_WORDS: Record<ReaderHealthState, string> = {
  online: "Online",
  offline: "Offline",
  not_seen: "Not seen",
  unpaired: "Unpaired",
};

/** The closeout's one line per reader: its status and last seen, or a dash for each part that is not known. A reader whose
 * health was not read at all is a dash. `formatInstant` turns an ISO instant into the screen's words. */
export function closeoutReaderLine(
  health: ReaderHealthDto | null | undefined,
  formatInstant: (iso: string) => string,
): string {
  if (health === null || health === undefined) {
    return "-";
  }
  const lastSeen = health.lastSeenAt === null ? "-" : formatInstant(health.lastSeenAt);
  return `${CLOSEOUT_STATE_WORDS[health.state]} · Last seen ${lastSeen}`;
}
